/**
 * Name search for the start step's two tabs.
 *
 * Both lists are already in memory — divisions and their nested teams arrive
 * whole with `GET /registration/:id` — so this filters locally rather than
 * calling anything. An empty query returns the list untouched, so the tab shows
 * its full contents before the reader types.
 */

import type { DivisionTeam, StartDivision } from './types'

/** Case- and whitespace-insensitive substring match. */
export const matchesName = (
  name: string | null | undefined,
  query: string | null | undefined
): boolean => {
  const needle = query?.trim().toLowerCase()
  if (!needle) {
    return true
  }
  return (name ?? '').toLowerCase().includes(needle)
}

/** Filter divisions by name. */
export const searchDivisions = <T extends StartDivision>(
  divisions: T[] | null | undefined,
  query: string | null | undefined
): T[] =>
  (divisions ?? []).filter((division) => matchesName(division?.name, query))

/**
 * Filter the team index by team name.
 *
 * Matching is on the team name only. The division name is shown on each row so
 * the reader can see where a team sits, but it is not searched — typing a
 * division name into the teams tab should return nothing rather than every team
 * in that division, which is what the divisions tab is for.
 */
export const searchDivisionTeams = <T extends StartDivision>(
  index: DivisionTeam<T>[] | null | undefined,
  query: string | null | undefined
): DivisionTeam<T>[] =>
  (index ?? []).filter((entry) => matchesName(entry?.team?.name, query))
